// Médicos — lista/perfil/criação + grade de horários.
// Tudo na API real (/clinica/doctors*): list/get/create(convite)/schedule/
// saveSchedule. Filtros de lista (q/specialty/status) aplicados no front.
import AXIOS from "@/services/axios";
import { initialsOf, avatarClassFor, search } from "./helpers";

const STATUS_LABELS = {
  ativo: "Ativo",
  onboarding: "Onboarding",
  inativo: "Inativo"
};

// day_of_week (0=dom … 6=sab) ↔ chave do front (dom/seg/…/sab).
const DAY_KEYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
// Ordem de exibição do front (segunda primeiro).
const DISPLAY_ORDER = ["seg", "ter", "qua", "qui", "sex", "sab", "dom"];

// Canônico do backend (work_days/day_of_week, almoço = 2 faixas) → shape da view.
function adaptScheduleFromApi(config) {
  if (!config) return null;
  const byDow = {};
  (config.work_days || []).forEach(wd => {
    byDow[Number(wd.day_of_week)] = wd.times || [];
  });
  const days = DISPLAY_ORDER.map(key => {
    const times = byDow[DAY_KEYS.indexOf(key)];
    if (!times || !times.length) {
      return {
        key,
        active: false,
        start: "08:00",
        end: "17:00",
        lunch: false,
        lunchStart: "12:00",
        lunchEnd: "13:00"
      };
    }
    if (times.length >= 2) {
      return {
        key,
        active: true,
        start: times[0].start_time,
        end: times[times.length - 1].end_time,
        lunch: true,
        lunchStart: times[0].end_time,
        lunchEnd: times[1].start_time
      };
    }
    return {
      key,
      active: true,
      start: times[0].start_time,
      end: times[0].end_time,
      lunch: false,
      lunchStart: "12:00",
      lunchEnd: "13:00"
    };
  });
  return {
    duration: Number(config.duration) || 30,
    gap: config.gap != null ? Number(config.gap) : 0,
    validity: { from: config.from_date || "", to: config.until_date || "" },
    days,
    blocks: (config.blocks || []).map(b => ({
      id: b.id,
      label: b.label,
      from: b.from,
      to: b.to
    }))
  };
}

// Shape da view → canônico do backend (só dias ativos; almoço vira 2 faixas).
function adaptScheduleToApi(payload) {
  const workDays = (payload.days || [])
    .filter(d => d.active)
    .map(d => ({
      day_of_week: DAY_KEYS.indexOf(d.key),
      times: d.lunch
        ? [
            { start_time: d.start, end_time: d.lunchStart },
            { start_time: d.lunchEnd, end_time: d.end }
          ]
        : [{ start_time: d.start, end_time: d.end }]
    }));
  return {
    duration: String(payload.duration),
    gap: payload.gap,
    from_date: payload.validity && payload.validity.from,
    until_date: payload.validity && payload.validity.to,
    work_days: workDays,
    blocks: (payload.blocks || []).map(b => ({
      id: b.id,
      label: b.label,
      from: b.from,
      to: b.to
    }))
  };
}

function adaptListItem(d, i) {
  return {
    id: d.id,
    name: d.name,
    photo: d.photo,
    initials: initialsOf(d.name),
    avatarClass: avatarClassFor(i),
    crm: d.crm,
    uf: d.uf,
    specialty: d.specialty,
    specialtyShort: d.specialty,
    status: d.status,
    statusLabel: STATUS_LABELS[d.status] || d.status,
    appointmentsMonth: d.appointments_month,
    occupation: d.occupation,
    consultPrice: d.consult_price_cents,
    // Orders pagas no mês (mesma fonte do Financeiro) — não estimado.
    billingMonth: d.billing_month_cents
  };
}

function adaptProfile(d) {
  return {
    id: d.id,
    name: d.name,
    photo: d.photo,
    initials: initialsOf(d.name),
    avatarClass: avatarClassFor(0),
    crm: d.crm,
    uf: d.uf,
    specialty: d.specialty,
    specialtyShort: d.specialty,
    status: d.status,
    statusLabel: STATUS_LABELS[d.status] || d.status,
    transferPct: d.transfer_pct,
    // De onde vem o %: contract | doctor_config | clinic_config | default.
    transferPctSource: d.transfer_pct_source,
    appointmentsMonth: d.appointments_month,
    occupation: d.occupation,
    consultPrice: d.consult_price_cents,
    // Dados administrativos reais (backend). Antes eram mock hardcoded.
    cpf: d.cpf,
    phone: d.phone,
    email: d.email,
    since: d.since,
    unit: d.unit,
    unitId: d.unit_id,
    conditions: d.specialty ? [d.specialty] : []
  };
}

export class ClinicDoctors {
  // Backend devolve todos os médicos da clínica; filtros (specialty/status/q)
  // são aplicados no front (a lista faz uma chamada filtrada + uma p/ KPIs).
  static list(params = {}) {
    return AXIOS.get("clinica/doctors").then(res => {
      let items = (res.data || []).map(adaptListItem);
      if (params.specialty && params.specialty !== "todas") {
        items = items.filter(d => d.specialty === params.specialty);
      }
      if (params.status && params.status !== "todos") {
        items = items.filter(d => d.status === params.status);
      }
      items = search(items, params.q, ["name", "crm", "specialty"]);
      return { data: items };
    });
  }

  static get(id) {
    return AXIOS.get(`clinica/doctors/${id}`).then(res => ({
      data: adaptProfile(res.data)
    }));
  }

  /** Contrato do médico com a clínica (admin): % de repasse e unidade. */
  static updateContract(id, { transferPct, unitId }) {
    return AXIOS.patch(`clinica/doctors/${id}/contract`, {
      transfer_pct: transferPct,
      unit_id: unitId
    }).then(res => ({ data: adaptProfile(res.data) }));
  }

  /** Convida um médico (admin). A clínica informa contato + contrato
   *  (repasse/unidade); CRM/UF/especialidade o médico informa no aceite.
   *  Aqui devolvemos um "médico" onboarding só para a tela de sucesso. */
  static create(payload) {
    return AXIOS.post("clinica/doctors/invite", {
      name: payload.name,
      email: payload.email,
      phone_number: payload.phone,
      transfer_pct: payload.transferPct,
      unit_id: payload.unitId
    }).then(res => ({
      data: {
        doctor: {
          id: res.data.id,
          name: res.data.name,
          specialty: res.data.specialty,
          status: res.data.status,
          statusLabel: STATUS_LABELS[res.data.status] || "Onboarding",
          initials: initialsOf(res.data.name)
        },
        invited: true,
        // e-mail já é de um médico: o convite aparece no painel dele
        existingAccount: !!res.data.existing_account
      }
    }));
  }

  /** Convites de médico ainda sem aceite (pendentes e recusados). Admin. */
  static invitations() {
    return AXIOS.get("clinica/doctors/invitations").then(({ data }) => ({
      data: (data || []).map(i => ({
        id: i.id,
        name: i.name,
        email: i.email,
        status: i.status,
        statusLabel: i.status === "recusado" ? "Recusou" : "Convidado",
        initials: initialsOf(i.name),
        createdAt: i.created_at ? String(i.created_at).slice(0, 10) : null
      }))
    }));
  }

  static resendInvitation(id) {
    return AXIOS.post(`clinica/doctors/invitations/${id}/resend`);
  }

  static cancelInvitation(id) {
    return AXIOS.delete(`clinica/doctors/invitations/${id}`);
  }

  static schedule(id) {
    return AXIOS.get(`clinica/doctors/${id}/schedule`).then(({ data }) => ({
      data: adaptScheduleFromApi(data)
    }));
  }

  static saveSchedule(id, payload) {
    return AXIOS.put(
      `clinica/doctors/${id}/schedule`,
      adaptScheduleToApi(payload)
    ).then(({ data }) => ({
      data: adaptScheduleFromApi(data)
    }));
  }
}

export default ClinicDoctors;
